// src/hooks/useMediaPermissions.js
import { useCallback, useEffect, useRef, useState } from "react"

const canQueryPermissions =
  typeof navigator !== "undefined" &&
  "permissions" in navigator &&
  typeof navigator.permissions.query === "function"

const isSecure = (() => {
  try {
    if (typeof window === "undefined") return false
    const host = window.location.hostname || ""
    const isLocalhost = /(^|\.)localhost$|^127\.0\.0\.1$|^::1$/.test(host)
    return window.isSecureContext || isLocalhost
  } catch { return false }
})()

async function deriveDeviceIdFromTrack(track, kind) {
  if (!track) return null
  // 1) Mejor fuente: settings.deviceId
  try {
    const id = track.getSettings?.().deviceId
    if (id) return id
  } catch {}
  // 2) Safari a veces no expone deviceId: buscamos por label tras permiso
  try {
    const label = track.label
    if (!label || !navigator?.mediaDevices?.enumerateDevices) return null
    const devs = await navigator.mediaDevices.enumerateDevices()
    const candidates = devs.filter(d => d.kind === (kind === "video" ? "videoinput" : "audioinput"))
    const match = candidates.find(d => d.label === label) || candidates[0]
    return match?.deviceId || null
  } catch { return null }
}

export function useMediaPermissions({ requireAudio = true, requireVideo = true, persistKey = "proctor" } = {}) {
  const [micState, setMicState] = useState("prompt")     // 'granted' | 'denied' | 'prompt' | 'unavailable'
  const [camState, setCamState] = useState("prompt")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const streamRef = useRef(null)
  const listeners = useRef([])

  // NUEVO: ids elegidos efectivamente
  const [selectedCamId, setSelectedCamId] = useState(localStorage.getItem(`${persistKey}.camId`) || null)
  const [selectedMicId, setSelectedMicId] = useState(localStorage.getItem(`${persistKey}.micId`) || null)

  const resetStream = () => {
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach(t => { try { t.stop() } catch {} }) } catch {}
      streamRef.current = null
    }
  }

  const detectSupport = () => {
    const hasMedia =
      typeof navigator !== "undefined" &&
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === "function"
    if (!hasMedia || !isSecure) {
      if (requireAudio) setMicState("unavailable")
      if (requireVideo) setCamState("unavailable")
    }
  }

  const queryPermission = useCallback(async (name) => {
    if (!canQueryPermissions) return "prompt"
    try {
      const status = await navigator.permissions.query({ name })
      if (name === "microphone") setMicState(status.state)
      if (name === "camera") setCamState(status.state)
      const onChange = () => {
        if (name === "microphone") setMicState(status.state)
        if (name === "camera") setCamState(status.state)
      }
      status.addEventListener?.("change", onChange)
      listeners.current.push(() => status.removeEventListener?.("change", onChange))
      return status.state
    } catch {
      // Safari/Firefox no soportan 'camera'/'microphone' en Permissions API
      return "prompt"
    }
  }, [])

  const precheck = useCallback(async () => {
    detectSupport()
    await Promise.all([
      requireAudio ? queryPermission("microphone") : Promise.resolve(),
      requireVideo ? queryPermission("camera") : Promise.resolve(),
    ])
  }, [queryPermission, requireAudio, requireVideo])

  const request = useCallback(async () => {
    setError("")
    setLoading(true)
    try {
      const constraints = {
        audio: !!requireAudio,
        video: !!requireVideo ? { facingMode: "user" } : false,
      }
      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      streamRef.current = stream

      // Estados de permiso
      if (requireAudio) setMicState("granted")
      if (requireVideo) setCamState("granted")

      // ⬇️ NUEVO: capturamos los deviceId reales y los persistimos
      const vTrack = requireVideo ? stream.getVideoTracks?.()[0] : null
      const aTrack = requireAudio ? stream.getAudioTracks?.()[0] : null

      const [camId, micId] = await Promise.all([
        deriveDeviceIdFromTrack(vTrack, "video"),
        deriveDeviceIdFromTrack(aTrack, "audio"),
      ])

      if (camId) {
        setSelectedCamId(camId)
        try { localStorage.setItem(`${persistKey}.camId`, camId) } catch {}
      }
      if (micId) {
        setSelectedMicId(micId)
        try { localStorage.setItem(`${persistKey}.micId`, micId) } catch {}
      }

      // Cerramos (solo era para permiso + descubrimiento)
      resetStream()
      return true
    } catch (e) {
      const name = e?.name || ""
      if (name === "NotAllowedError" || name === "SecurityError") {
        if (requireAudio) setMicState("denied")
        if (requireVideo) setCamState("denied")
        setError("Has denegado el acceso. Revisa los permisos del sitio en tu navegador.")
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        setError("No se detectó micrófono o cámara conectados.")
        if (requireAudio) setMicState("unavailable")
        if (requireVideo) setCamState("unavailable")
      } else {
        setError(e?.message || "No se pudieron solicitar los permisos.")
      }
      return false
    } finally {
      setLoading(false)
    }
  }, [requireAudio, requireVideo, persistKey])

  useEffect(() => {
    precheck()
    return () => {
      resetStream()
      listeners.current.forEach(off => { try { off() } catch {} })
      listeners.current = []
    }
  }, [precheck])

  const allGranted =
    (!requireAudio || micState === "granted") &&
    (!requireVideo || camState === "granted")

  return {
    micState, camState, error, loading, request, allGranted, secure: isSecure,
    selectedCamId, selectedMicId,   // ⬅️ EXPORTADO
  }
}
